import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ListingProofContent } from './landingProofContent';

interface LandingListingProofProps {
  content: ListingProofContent;
  onGetStarted: () => void;
}

export function LandingListingProof({
  content,
  onGetStarted,
}: LandingListingProofProps) {
  return (
    <section className="relative overflow-hidden bg-charcoal px-6 py-24 text-alabaster md:py-32">
      <div className="absolute left-12 top-16 h-40 w-40 rounded-full bg-terracotta/10 blur-3xl" />
      <div className="absolute bottom-12 right-10 h-56 w-56 rounded-full bg-olive/15 blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[0.88fr_1.12fr]">
        <div className="space-y-8">
          <div className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-mono uppercase tracking-[0.28em] text-sand">
            {content.eyebrow}
          </div>
          <div className="space-y-5">
            <h2 className="max-w-2xl text-4xl font-serif font-light tracking-tight md:text-6xl">
              {content.title}
            </h2>
            <p className="max-w-xl text-lg font-light leading-relaxed text-alabaster/75 md:text-xl">
              {content.description}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {content.metrics.map((metric) => (
              <span
                key={metric}
                className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-light text-alabaster/85"
              >
                {metric}
              </span>
            ))}
          </div>

          <div className="grid gap-3">
            {content.supportingPoints.map((point) => (
              <div key={point} className="flex items-start gap-3 text-sm font-light text-alabaster/75">
                <span className="mt-1 h-2 w-2 rounded-full bg-terracotta" />
                <span>{point}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <button
              onClick={onGetStarted}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-alabaster px-8 py-4 text-base font-medium text-charcoal transition-all duration-500 hover:-translate-y-0.5 hover:bg-white hover:shadow-lift"
            >
              Start staging your hero image
              <ArrowRight className="h-4 w-4" />
            </button>
            <Link
              to="/virtual-staging/before-after"
              className="inline-flex items-center gap-2 border-b border-alabaster/30 pb-1 text-base font-light text-alabaster/70 transition-all duration-300 hover:border-alabaster/60 hover:text-alabaster"
            >
              Browse more listing transformations
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="relative mx-auto min-h-[34rem] w-full max-w-3xl">
          <div className="absolute left-0 top-10 z-10 w-44 rotate-[-8deg] overflow-hidden rounded-[1.75rem] border border-white/10 bg-white/95 shadow-soft-xl md:w-56">
            <div className="border-b border-stone/70 px-4 py-3">
              <div className="text-xs font-mono uppercase tracking-[0.24em] text-sand">
                {content.comparisonCard.title}
              </div>
              <div className="mt-1 text-sm font-light text-charcoal/70">
                {content.comparisonCard.subtitle}
              </div>
            </div>
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={content.comparisonCard.image}
                alt={content.comparisonCard.title}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </div>

          <div className="relative z-20 ml-auto max-w-xl overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-[0_30px_90px_-30px_rgba(0,0,0,0.55)]">
            <div className="flex items-center justify-between border-b border-stone/70 px-5 py-4">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.24em] text-sand">
                  {content.mainCard.title}
                </div>
                <div className="mt-1 text-sm font-light text-charcoal/70">
                  {content.mainCard.subtitle}
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                {content.mainCard.badges.map((badge) => (
                  <span
                    key={badge}
                    className="rounded-full bg-olive/10 px-3 py-1 text-xs font-mono uppercase tracking-[0.16em] text-olive"
                  >
                    {badge}
                  </span>
                ))}
              </div>
            </div>
            <div className="aspect-[4/3] overflow-hidden bg-stone/20">
              <img
                src={content.mainCard.image}
                alt={content.mainCard.title}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="grid gap-3 border-t border-stone/70 px-5 py-4 sm:grid-cols-3">
              {content.metrics.map((metric) => (
                <div key={metric} className="rounded-2xl bg-alabaster px-3 py-3 text-sm font-light text-charcoal/80">
                  {metric}
                </div>
              ))}
            </div>
          </div>

          <div className="absolute bottom-0 right-0 z-30 w-48 rotate-[7deg] overflow-hidden rounded-[1.75rem] border border-white/10 bg-white shadow-soft-xl md:w-64">
            <div className="border-b border-stone/70 px-4 py-3">
              <div className="text-xs font-mono uppercase tracking-[0.24em] text-sand">
                {content.socialCard.title}
              </div>
              <div className="mt-1 text-sm font-light text-charcoal/70">
                {content.socialCard.subtitle}
              </div>
            </div>
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={content.socialCard.image}
                alt={content.socialCard.title}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex items-center justify-between px-4 py-3 text-xs font-mono uppercase tracking-[0.2em] text-sand">
              <span>Feed crop</span>
              <span>Mobile-safe</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
